import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {GraphVariableModel} from "../../shared/models/graph-variable.model";
import {GraphService} from "../../shared/services/graph.service";
import {Subscription} from "rxjs";
import {CookieService} from "ngx-cookie-service";
import {DataAvailabilityService} from "../../shared/services/data-availability.service";
import {CollectionModel} from "../../shared/models/collection.model";
import {VariablesPublicNamesService} from "../../shared/services/variables-public-names.service";
import {VariablePublicNamesMap} from "../../shared/utils/variable-display-name";
import {take} from "rxjs/operators";

@Component({
  selector: 'app-graph-selected-variables-list',
  templateUrl: './graph-selected-variables-list.component.html',
  styleUrls: ['./graph-selected-variables-list.component.scss']
})
export class GraphSelectedVariablesListComponent implements OnInit, OnDestroy {
  @Input() componentContext: 'graph-page' | 'dataset-page';
  @Input() collection: CollectionModel | null; // Used in dataset page context
  /**
   * #24 — the chart-eligible variables for the dataset page, or null on the graph page
   * where the concept does not apply. Defence in depth only: `ProductPageComponent` has
   * already pruned the stored selection before this component can exist, so this keeps the
   * table consistent if the stored list is mutated later in the session.
   */
  @Input() chartEnabledKeys: string[] | null = null;
  selectedVariablesSubscription: Subscription;
  collectionDataAvailabilitySubscription: Subscription;
  selectedVariables: GraphVariableModel[];
  /**
   * #23 — configured public variable names, keyed by technical identifier. The table's
   * Column cell is resolved through this at render time; the stored `column` on each row
   * is never rewritten, because it is the storage/query/request key.
   */
  publicNamesMap: VariablePublicNamesMap = {};
  displayedColumns = [
    'station',
    'collectionName',
    'column',
    'fromDate',
    'singleDepth',
    'fromDepth',
    'toDepth',
    'depthAggregation',
    'dataType',
    'timeSteps',
    'options',
  ];

  depthAggregations = ['max', 'min', 'mean', 'sum', 'integration'];
  dataAggregations = ['raw', 'max', 'min', 'mean', 'sum'];
  timeSteps = ['weekly', 'monthly', 'yearly'];
  availableSingleDepths: number[] = [];
  availableDateMin: Date = null;
  availableDateMax: Date = null;

  constructor(
    private graphService: GraphService,
    private dataAvailabilityService: DataAvailabilityService,
    private variablesPublicNamesService: VariablesPublicNamesService
  ) {
  }

  ngOnInit(): void {
    this.getPublicVariableNames();
    this.loadGraphVariables();
    this.subscribeToGraphDataSubject();
    if (this.componentContext === 'dataset-page' && this.collection) {
      const indexes = this.selectedVariables
        .map((item, index) => this.belongsOnDatasetPage(item) ? -1 : index)
        .filter(index => index !== -1);

      // Remove from last to first to prevent index shifting
      indexes.sort((a, b) => b - a).forEach(index => {
        this.removeItem(index);
      });
    }
  }

  /**
   * #23 — load the configured public names used to label the Column cell. `take(1)` per
   * init and no shared cache, matching the graph form: an Admin rename shows up on the
   * next page load without an application reload.
   */
  private getPublicVariableNames(): void {
    this.variablesPublicNamesService.getVariablesPublicNames()
      .pipe(take(1))
      .subscribe(map => this.publicNamesMap = map ?? {});
  }

  /**
   * A stored row belongs on this dataset page when it is this collection's *and* — #24 —
   * its variable is still chart-eligible. `chartEnabledKeys` is null on the graph page and
   * on any caller that does not supply it, where only the collection check applies.
   */
  private belongsOnDatasetPage(item: GraphVariableModel): boolean {
    if (item?.collectionName !== this.collection.title) {
      return false;
    }
    return this.chartEnabledKeys === null || this.chartEnabledKeys.includes(item?.column);
  }

  loadGraphVariables(): void {
    const selectedVariablesKey = this.graphService.getSelectedVariablesKey(this.componentContext);
    this.selectedVariables = this.graphService.getLocalStorageSelectedVariables(selectedVariablesKey);
    this.refreshAvailableDepths(this.selectedVariables);
  }

  /**
   * Fetch the available depths (and date bounds) for the given variables and populate
   * `availableSingleDepths`, which drives the depth <mat-select> options in the table.
   *
   * Must run not only on init but also whenever the graph variables change (e.g. after
   * Visualize). Otherwise the options list stays empty and the selected depth cannot be
   * rendered — even though it is correctly stored on the row — because a mat-select
   * cannot display a value that has no matching <mat-option>.
   */
  private refreshAvailableDepths(variables: GraphVariableModel[]): void {
    if (!variables || variables.length === 0) {
      return;
    }

    // Prepare payload
    const requestData: { [collectionName: string]: string[] } = {};

    for (const variable of variables) {
      if (!requestData[variable.collectionName]) {
        requestData[variable.collectionName] = [];
      }

      // Avoid duplicates
      if (!requestData[variable.collectionName].includes(variable.column)) {
        requestData[variable.collectionName].push(variable.column);
      }
    }

    if (Object.keys(requestData).length === 0) {
      return;
    }

    // Avoid leaking/racing a previous in-flight availability request
    if (this.collectionDataAvailabilitySubscription) {
      this.collectionDataAvailabilitySubscription.unsubscribe();
    }

    this.collectionDataAvailabilitySubscription = this.dataAvailabilityService
      .collectionAvailableDataByCollectionVariables(requestData)
      .subscribe(collections => {
        if (collections.length > 0) {
          const depthSet = new Set<number>();
          let globalFromDate: Date = null;
          let globalToDate: Date = null;

          collections.forEach(collection => {
            Object.values(collection).forEach((records: any) => {
              if (Array.isArray(records)) {
                records.forEach(record => {
                  // Date range
                  const from = new Date(record.fromDate);
                  const to = new Date(record.toDate);

                  if (!globalFromDate || from < globalFromDate) globalFromDate = from;
                  if (!globalToDate || to > globalToDate) globalToDate = to;

                  // Depths
                  if (Array.isArray(record.depths)) {
                    record.depths.forEach((d: number) => depthSet.add(d));
                  }
                });
              }
            });
          });

          this.availableSingleDepths = Array.from(depthSet).sort((a, b) => a - b);
          this.availableDateMin = globalFromDate;
          this.availableDateMax = globalToDate;
        }
      });
  }

  private subscribeToGraphDataSubject(): void {
    this.selectedVariablesSubscription = this.graphService.graphVariablesSubject.subscribe((graphVariables: GraphVariableModel[]) => {
      this.selectedVariables = graphVariables;
      // Refresh the depth/date option lists for the newly visualized variables so the
      // selected depth renders immediately (previously only populated on ngOnInit).
      this.refreshAvailableDepths(graphVariables);
    })
  }

  onEditVariable(changedComponentItem: GraphVariableModel, componentIndex: number): void {
    const variablesKey = this.graphService.getSelectedVariablesKey(this.componentContext);
    const variables = this.graphService.getLocalStorageSelectedVariables(variablesKey);
    if (changedComponentItem.dataType !== 'raw' && !changedComponentItem.timeSteps) {
      changedComponentItem.timeSteps = 'monthly';
    }
    variables[componentIndex] = changedComponentItem;
    this.graphService.setLocalStorageSelectedVariables(variablesKey, variables)
    this.graphService.graphVariablesSubject.next(variables)
  }

  onStartDateChange(newDate: Date | null, element: any, index: number): void {
    if (newDate) {
      element.fromDate = newDate;
      this.onEditVariable(element, index);
    }
  }

  onEndDateChange(newDate: Date | null, element: any, index: number): void {
    if (newDate) {
      element.toDate = newDate;
      this.onEditVariable(element, index);
    }
  }

  removeItem(index: number): void {
    const selectedVariablesKey = this.graphService.getSelectedVariablesKey(this.componentContext);
    let variables = this.graphService.getLocalStorageSelectedVariables(selectedVariablesKey);
    const variablesKey = this.graphService.getSelectedVariablesKey(this.componentContext);
    if (variables.length > 1) {
      variables.splice(index, 1);
      this.graphService.setLocalStorageSelectedVariables(variablesKey, variables)
    } else {
      this.graphService.deleteLocalStorageSelectedVariables(variablesKey);
      variables = [];
    }

    this.graphService.graphVariablesSubject.next(variables);
    this.graphService.resetGraph.next(true);
  }

  ngOnDestroy(): void {
    if (this.selectedVariablesSubscription) {
      this.selectedVariablesSubscription.unsubscribe();
    }
    if (this.collectionDataAvailabilitySubscription) {
      this.collectionDataAvailabilitySubscription.unsubscribe();
    }
  }
}
